import { ListCallsIO } from '../types';
import {
  UseCallIntelV2InfiniteQuery,
  UseCallIntelV2InfiniteQueryEndpointArgs,
} from './use-call-intelligence-v2-infinite-query';

type ListCallsPageParams = {
  offset: number;
  limit: number;
};
/**
 * A hook that returns an infinite query for the `ListCalls` endpoint.
 * @param request The request object to pass to the query. Overriding pagination fields on the request is handled by the hook.
 * @param options (optional) The options to pass to `useInfiniteQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListCallsQuery = <E = unknown, D = ListCallsIO['output']>({
  options,
  request,
  ...args
}: UseCallIntelV2InfiniteQueryEndpointArgs<'ListCalls', E, D, ListCallsPageParams>) => {
  const { limit: initialLimit = 25 } = request;

  return UseCallIntelV2InfiniteQuery<'ListCalls', E, D, ListCallsPageParams>({
    endpointName: 'ListCalls',
    ...args,
    request: ({ pageParam }) => {
      const requestWithPageParams: ListCallsIO['input'] = {
        ...request,
        offset: pageParam?.offset || request.offset,
        limit: pageParam?.limit || request.limit,
      };
      return requestWithPageParams;
    },
    options: {
      ...options,
      getNextPageParam: (lastPage, allPages) => {
        const count = lastPage.calls?.length ?? 0;
        if (count < initialLimit) return undefined;
        return {
          offset: allPages.length * initialLimit,
          limit: initialLimit,
        };
      },
      getPreviousPageParam: (_, allPages) => {
        const pageCount = allPages.length;
        if (pageCount <= 1) return undefined;
        return {
          offset: (pageCount - 2) * initialLimit,
          limit: initialLimit,
        };
      },
    },
  });
};
